import { useInfiniteQuery } from 'react-query';
import {
  generateFilter,
  generateIncludeQuery,
  generatePaginationQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { fetchWrapper } from 'utils/fetchWrapper';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import type {
  SparkRunInputParameterRecommendationsSource,
  SparkRunInputParameterRecommendationsSourceResponseInclude,
} from 'types/Spark/SparkRunInputParameterRecommendationsSource';
import toast from 'react-hot-toast';
import { INFINITE_CACHE_AND_STALE_TIME } from 'constants/index';
import type { Memory } from 'types/Memory';

const getRecommendationSources = (
  sparkInputParameterID: string | undefined,
  pageNumber: number
) => {
  if (!sparkInputParameterID) {
    return Promise.reject('input parameter id is required');
  }

  const queryArray = [
    generateFilter(
      'sparkRunInputParameterRecommendationID',
      sparkInputParameterID
    ),
    generateIncludeQuery(['memory']),
    generatePaginationQuery({
      pageNumber,
      pageSize: 5,
    }),
  ];

  const url = generateURL(
    SPARK_RESOURCE_TYPES.SPARK_RUN_INPUT_PARAMETER_RECOMMENDATIONS_SOURCE,
    queryArray
  );

  return fetchWrapper<SparkRunInputParameterRecommendationsSourceResponseInclude>(
    {
      url,
    }
  );
};

export const useInfiniteSparkRunRecommendationSourcesByInputParameterID = (
  inputParameterID: string | undefined
) => {
  const { data, ...rest } = useInfiniteQuery({
    queryKey: ['spark-recommendation-sources', inputParameterID],
    queryFn: ({ pageParam = 1 }) =>
      getRecommendationSources(inputParameterID, pageParam),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.data.length < 5 ? undefined : allPages.length + 1;
    },
    enabled: !!inputParameterID,
    ...INFINITE_CACHE_AND_STALE_TIME,
    onError: (error) => {
      if (error instanceof Error) {
        toast.error('Failed to fetch Input Parameters Recommendation sources');
      }
    },
  });

  //@ts-ignore
  const totalSources = data?.pages[0].meta.total;
  const recommendationWithSources = data?.pages.reduce<
    Array<
      Memory & {
        recommendation: SparkRunInputParameterRecommendationsSource | undefined;
      }
    >
  >((acc, current) => {
    if (!current.data) {
      return acc;
    }

    const recommendationMemory = current.included.map((memory) => {
      return {
        ...memory,
        recommendation: current.data.find(
          (recommendation) => recommendation.attributes.memoryID === memory.id
        ),
      };
    });

    return [...acc, ...recommendationMemory];
  }, []);

  return {
    recommendationWithSources,
    totalSources,
    ...rest,
  };
};
